import League from "../entities/League.js";
import RankingStat from "../entities/RankingStat.js";
import * as Enums from "../enums/index.js";
import SessionFormatter from "../formatters/SessionFormatter.js";
import RankingStatsGenerator from "../generators/RankingStatsGenerator.js";
import ResultsSlicesGenerator from "../generators/ResultsSlicesGenerator.js";

/**
 * Provides calculation of various statistical parameters
 */
class RankingStatsProvider {
    constructor(rankingStatsGenerator, resultsSlicesGenerator, sessionFormatter, returnScopesWithoutParams, throwOnErrors) {
        if (!(rankingStatsGenerator instanceof RankingStatsGenerator)) {
            throw new TypeError('rankingStatsGenerator must be an instance inheriting from RankingStatsGenerator class');
        }
        this.rankingStatsGenerator = rankingStatsGenerator;

        if (!(resultsSlicesGenerator instanceof ResultsSlicesGenerator)) {
            throw new TypeError('resultsSlicesGenerator must be an instance inheriting from ResultsSlicesGenerator class');
        }
        this.resultsSlicesGenerator = resultsSlicesGenerator;

        if (typeof(sessionFormatter) !== 'undefined' && !(sessionFormatter instanceof SessionFormatter)) {
            throw new TypeError('sessionFormatter must be an instance of SessionFormatter class');
        }
        if (typeof(throwOnErrors) === 'undefined') {
            throwOnErrors = false;
        }
        this.sessionFormatter = sessionFormatter;

        this.returnScopesWithoutParams = !!returnScopesWithoutParams;
        this.throwOnErrors = !!throwOnErrors;
    }

    /**
     * 
     * @param {object} session  https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/session-v2.json?at=feature%2Fbaseball
     * @param {League} league https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/league.json?at=feature%2Fbaseball
     * @returns []
     */
    * calculateStats(session, league) {
        if (!session || typeof(session) !== 'object') {
            throw new TypeError('session must be an object');
        }

        if (!(session.results instanceof Array)) {
            throw new TypeError('session.results must be an array containing session results');
        }

        if (!session.rules || typeof(session.rules) !== 'object') {
            throw new TypeError('session.rules must be an object containing session rules');
        }

        if (typeof(session.parameters) !== 'object') {
            throw new TypeError('session.parameters must be an object'); 
        }

        if (session.parameters.SessionContents && !Enums.SessionContents.hasOwnProperty(session.parameters.SessionContents)) {
            throw new TypeError('session.parameters.SessionContents must be undefined or an string from sessionContents enum');
        }

        if (!(session.lineups instanceof Array)) {
            throw new TypeError('session.lineups must be an array containing session lineups');
        }

        if (!(league instanceof League)) {
            throw new TypeError('league must be an instance inheriting from League entity');
        }

        // so... let's do it
        yield* this.processResults(
            // session could need some small formatting...
            this.formatSession(session),
            league
        );
    }

    * processResults(session, league) {
        const sliceWarnings = [];
        const slices = this.resultsSlicesGenerator.generate(session, league, sliceWarnings);

        for (const slice of slices) {
            const errors = [];
            const stats = this.rankingStatsGenerator.generate(
                slice, session, league, errors
            );

            if (this.throwOnErrors) {
                if (errors.length) {
                    for (let i = 0; i < errors.length; i++) {
                        throw errors[i].error;
                    }
                }

                /*
                if (sliceWarnings.length) {
                    for (let i = 0; i < sliceWarnings.length; i++) {
                        throw sliceWarnings[i];
                    }
                } */
            }

            const parameters = [];
            for (const stat of stats) {
                if (!stat) {
                    //throw new TypeError(`statistical value wasn't calculated for ${slice.scope.toString()}`);
                    continue;
                }
                if (!(stat instanceof RankingStat)) {
                    throw new TypeError(`statistical value wasn't calculated for ${slice.scope.toString()} - empty object returned`);
                }

                parameters.push(stat);
            }

            if (this.returnScopesWithoutParams || parameters.length) {
                if (sliceWarnings.length) {
                    errors.push(...sliceWarnings.map(sliceError => {
                        return {
                            error: sliceError,
                            stat: null,
                            calculator: this.resultsSlicesGenerator
                        }
                    }));
    
                    sliceWarnings.splice(0, sliceWarnings.length);
                }

                yield {
                    scope: slice.scope,
                    parameters,
                    errors: [].concat(errors)
                };
            }
        }

        if (sliceWarnings.length && this.throwOnErrors) {
            for (let i = 0; i < sliceWarnings.length; i++) {
                throw sliceWarnings[i];
            }
        }

        // sliceWarnings.splice(0, sliceWarnings.length);
    }

    formatSession(session) {
        return this.sessionFormatter ? this.sessionFormatter.format(session) : session;
    }
}

export default RankingStatsProvider;